/**
 * `?lang=en|es` for anonymous visitors (T23).
 *
 * A QR code or a campaign link can fix the interface language: the Spanish
 * flyer opens the site in Spanish whatever the phone's own language is. The
 * value is written where the EN/ES switch writes it (i18next's localStorage
 * cache, `fixydany_lang`), so it sticks for the next visit and an account
 * created afterwards starts in it — signup sends `i18n.language`, exactly as
 * it does after a manual switch.
 *
 * **Signed-in users are left alone.** Their language is a profile preference
 * (push and email are rendered from it), and a flyer is not the place to
 * change it. The catch is that the session is an httponly cookie: nothing in
 * the browser can tell, before the first render, whether there is one. So the
 * parameter is applied optimistically at boot — the anonymous visitor, the
 * person this exists for, sees the right language on the first paint — and
 * `settleLangParam` undoes it when `/auth/me` answers with a user. The undo
 * runs before `setUser`, so the profile sync in AuthContext never sees the
 * flyer's language. A signed-in user who opens such a link can see the
 * other language for the length of that one request.
 *
 * The parameter stays in the URL and nothing redirects.
 */

/** The i18next detector's `lookupLocalStorage`, shared with `@/i18n`. */
export const LANG_STORAGE_KEY = "fixydany_lang";

const LOCALES = { en: "en-US", es: "es-US" };

// What applyLangParam replaced, until /auth/me says whether to keep it.
let pending = null;

function readStored() {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

// The detector caches inside changeLanguage without a try of its own, so a
// storage that starts throwing after init would surface here.
function switchTo(i18n, locale) {
  try {
    i18n.changeLanguage(locale);
  } catch (e) { /* the page keeps its language; nothing else to do */ }
}

function writeStored(value) {
  try {
    if (value === null) window.localStorage.removeItem(LANG_STORAGE_KEY);
    else window.localStorage.setItem(LANG_STORAGE_KEY, value);
  } catch (e) { /* storage off: the language still applies to this page */ }
}

/** Apply `?lang=` before the first render. Only "en" and "es" are accepted. */
export function applyLangParam(i18n) {
  let locale;
  try {
    locale = LOCALES[new URLSearchParams(window.location.search).get("lang")];
  } catch (e) {
    return;
  }
  if (!locale) return;

  pending = { i18n, stored: readStored(), language: i18n.language };
  switchTo(i18n, locale);
  writeStored(locale);
}

/** Called once the session is known. Signed in: put everything back. */
export function settleLangParam(signedIn) {
  if (!pending) return;
  const { i18n, stored, language } = pending;
  pending = null;
  if (!signedIn) return;
  switchTo(i18n, language);
  // After changeLanguage, which caches what it was given: the key may not
  // have existed (a language detected from the browser), and stays that way.
  writeStored(stored);
}
